import React from "react";
import {
    Paper,
    Typography,
    Button,
    Chip,
    Stack,
    Box,
    CircularProgress,
    IconButton,
    Tooltip
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import default_theme from "assets/themes/themes";

const getDraftTitle = (data) =>
    data.title_en || data.title_english || data.title || "Untitled Draft";

const isRecentPublishedAt = (publishedAt) => {
    if (!publishedAt) return false;

    const publishedDate = new Date(publishedAt);
    if (Number.isNaN(publishedDate.getTime())) return false;

    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);

    return publishedDate >= twoMonthsAgo;
};

const DraftSolutionCard = ({
    draft,
    onView,
    onAddToPhase3_3,
    onEdit,
    onDelete,
    onUnpublish,
    isPublished = false,
    isSelectedForPhase3_3 = false,
    isSelectingPhase3_3 = false,
    isDeleting = false,
}) => {
    const data = draft.combinedData || {};
    const showNewTag = isRecentPublishedAt(data.published_at);

    return (
        <Paper
            sx={{
                p: 2.5, 
                borderRadius: 3,
                position: "relative",
                overflow: "hidden",
                border: "1px solid #23576B",
                boxShadow: `0 10px 26px ${default_theme.palette.other.hoverLight}`,
                background: `linear-gradient(135deg, ${default_theme.palette.primary.main} 0%, ${default_theme.palette.other.backgroundGrey} 100%)`,
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                opacity: isDeleting ? 0.6 : 1, 
                "&:hover": {
                    transform: isDeleting ? "none" : "translateY(-2px)",
                    boxShadow: isDeleting ? "none" : `0 14px 30px rgba(35, 87, 107, 0.14)`,
                },
            }}
        >
            <Box>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1.5}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color: default_theme.palette.other.addButtonBlue,
                                lineHeight: 1.2,
                                wordBreak: "break-word",
                            }}
                        >
                            {getDraftTitle(data)}
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{
                                mt: 1,
                                color: default_theme.palette.other.textSecondary,
                                display: '-webkit-box',
                                WebkitLineClamp: 5,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                wordBreak: 'break-word'
                                
                            }}
                        >
                            
                            {data.story || "Draft solution ready for review"}
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {/* The NEW tag stays exactly the same */}
                    {showNewTag && (
                        <Chip
                            label="NEW"
                            sx={{
                                fontWeight: 800,
                                color: default_theme.palette.primary.main,
                                backgroundColor: default_theme.palette.other.chatGreen,
                            }}
                        />
                    )}

                    {/* The updated RDD logic to split multiple selections into separate tags */}
                    {data.rdd ? (
                        (Array.isArray(data.rdd) ? data.rdd : data.rdd.split(","))
                            .map((rddItem, index) => (
                                <Chip
                                    key={index}
                                    label={rddItem.trim()}
                                    sx={{
                                        fontWeight: 700,
                                        color: default_theme.palette.other.addButtonBlue,
                                        backgroundColor: default_theme.palette.other.tealBackgroundSoft,
                                    }}
                                />
                            ))
                    ) : (
                        <Chip
                            label="No RDD"
                            sx={{
                                fontWeight: 700,
                                color: default_theme.palette.other.addButtonBlue,
                                backgroundColor: default_theme.palette.other.tealBackgroundSoft,
                            }}
                        />
                    )}
                </Box>
                </Stack>

                {/* --- ACTION BUTTONS (BOTTOM RIGHT) --- */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 3, gap: 2 }}>
                    
                    {/* Publish Button (Only shows if NOT published) */}
                    {!isPublished && onAddToPhase3_3 && (
                        <Button
                            variant={isSelectedForPhase3_3 ? "outlined" : "contained"}
                            onClick={() => onAddToPhase3_3(draft)}
                            disabled={isSelectingPhase3_3}
                            sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: 2,
                            backgroundColor: isSelectedForPhase3_3 ? "transparent" : default_theme.palette.other.chatGreen,
                            color: isSelectedForPhase3_3 ? default_theme.palette.other.chatGreen : "#fff",
                            borderColor: default_theme.palette.other.chatGreen,
                            "&:hover": {
                                backgroundColor: isSelectedForPhase3_3 ? "#E8F5E9" : default_theme.palette.other.chatGreenHover,
                            },
                            }}
                        >
                            {isSelectedForPhase3_3 ? "Attached to LAP" : "Attach to LAP"}
                        </Button>
                        )}
                    {/* Unpublish Button (Only shows if PUBLISHED) */}
                    {isPublished && onUnpublish && (
                        <Button
                            variant="outlined"
                            onClick={() => onUnpublish(draft)}
                            sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 700,
                                color: default_theme.palette.other.orange,
                                borderColor: default_theme.palette.other.orange,
                                "&:hover": {
                                    backgroundColor: default_theme.palette.other.lightOrange,
                                    borderColor: default_theme.palette.other.orange,
                                },
                            }}
                        >
                            Move Back to Drafts
                        </Button>
                    )}

                    {/* Group: View Details, Edit, Delete */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Button 
                            variant="outlined" 
                            onClick={onView}
                            sx={{ 
                                textTransform: "none", 
                                fontWeight: 600, 
                                borderRadius: 2,
                                color: "#23576B",
                                borderColor: "#23576B",
                                "&:hover": {
                                    backgroundColor: "#f4f7f9"
                                }
                            }}
                        >
                            View Details
                        </Button>

                        <Tooltip title="Edit">
                            <IconButton onClick={onEdit} sx={{ color: "#23576B" }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                            <IconButton onClick={onDelete} disabled={isDeleting} sx={{ color: "#d32f2f" }}>
                                {isDeleting ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    <DeleteOutlineIcon fontSize="small" />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>

                </Box>
            </Box>
        </Paper>
    );
};

export default DraftSolutionCard;